import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames/bind';
import axios from 'axios';
import PropTypes from 'prop-types';

import { setCookie } from 'utils/cookies';
import { browserReliableDetection } from 'utils/browserReliableDetection';
import { DataBlocks, Heading, I18n, Status, Button, Icon } from 'components';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Notifications } from '@beef/ui-kit';
import {
  getEsiaUsisData,
  initAfterCheckStatusEsia,
  redirectByEsiaAutoUrl,
  redirectByEsiaOnceUrl,
} from '../../requests/esiaUsis';

import {
  removeNotificationPD,
  setIsFetchError,
  setUsisData,
  toggleUsisModal,
} from '../../containers/PassportUpload/actions';
import {
  selectEsiaUsisMainParamsOnce,
  selectIsFetchError, selectIsModalOpened, selectIsOnlyOnceVariantUpd,
  selectIsPersDataAutoUpdateEnabled,
  selectIsWaitingRequest, selectNotificationDetails, selectNotifications,
} from '../../containers/PassportUpload/selectors';
import { checkUrlStatusAfterEsia } from './utils';
import styles from './styles.pcss';

const cx = classNames.bind(styles);

const UpdateDataFromEsia = ({
  esiaUrl = '/api/profile/userInfo/esiaData/',
  esiaDataUrl,
  actions,
  isPersDataAutoUpdateEnabled,
  isWaitingRequest,
  requiredAction,
  isFetchError,
  notifications,
  notificationDetails,
  isOnlyOnceVariantUpd,
  onceParams,
}) => {
  const [pending, setPending] = useState(false);
  const [isIE, setIsIE] = useState(false);

  const dispatch = useDispatch();

  useEffect(async () => {
    try {
      setPending(true);
      await dispatch(getEsiaUsisData());
      await dispatch(checkUrlStatusAfterEsia());
    } catch (error) {
      console.error(`Error fetch data in UpdateDataFromEsia. ERROR: ${error}`);
    } finally {
      setPending(false);
    }

    setIsIE(browserReliableDetection() === 'IE');
  }, []);

  useEffect(() => {
    setPending(true);

    if (isPersDataAutoUpdateEnabled) {
      actions?.initAfterCheckStatusEsia(requiredAction);
      setPending(false);
    }
  }, [isPersDataAutoUpdateEnabled]);

  const esiaUrlHandleClick = useCallback(() => {
    window.open(esiaUrl, '_blank');
    setCookie({
      name: '_usis_waiting_3871',
      value: 1,
      maxAge: 1800,
    });
    return null;
  }, []);

  const getEsiaUrl = useCallback(() => {
    if (isWaitingRequest) return null;
    if (isPersDataAutoUpdateEnabled) {
      if (isOnlyOnceVariantUpd) { // если не вызвался /api/profile/userinfo/esiastate/ - checkStatusEsiaConsent
        actions?.redirectByEsiaOnceUrl(onceParams);
        return;
      }
      actions?.toggleUsisModal(true);
      return;
    }

    setPending(true);

    axios.get(esiaDataUrl)
      .then(({ data }) => {
        if (data.isSucceeded) {
          window.open(data.url, '_blank');
          setCookie({
            name: '_usis_waiting_3871',
            value: 1,
            maxAge: data.expires || 1800,
          });
          return null;
        }
      })
      .catch(() => {
        setIsFetchError(true);
      });

    setPending(false);
  }, [isPersDataAutoUpdateEnabled, requiredAction, isFetchError, isWaitingRequest]);

  return (
    <DataBlocks.Container>
      <div className={cx('container')}>
        <Icon name="gosLogo" className={cx('icon')} />

        <div className={cx('case')}>
          <Heading level={4} tagName="h5" className={cx('heading')}>
            <I18n path="settings.passportUpload.headingSecondCase" />
          </Heading>

          <I18n className={cx('description')} path="settings.passportUpload.descriptionSecondCase" />

          {isIE ? (
            <Status className={[cx('warning', 'warning--unsupported'), 'attention']}>
              <I18n path="settings.passportUpload.unsupportedBrowser" />
            </Status>
          ) : (
            <Button
              color="light"
              className={cx('button')}
              onClick={esiaDataUrl ? getEsiaUrl : esiaUrlHandleClick}
              isLoading={pending}
              disabled={pending}
            >
              <I18n path="settings.passportUpload.buttonSecondCaseUpdate" />
            </Button>
          )}

          {notifications && isFetchError && (
            <Notifications
              text="Ошибка"
              variant="primary"
              isWithCloseButton
              notifications={notifications}
              duration={notificationDetails?.duration}
              onClick={actions.removeNotificationPD}
            />
          )}
        </div>
      </div>
    </DataBlocks.Container>
  );
};

UpdateDataFromEsia.propTypes = {
  esiaUrl: PropTypes.string,
  esiaDataUrl: PropTypes.string,
  requiredAction: PropTypes.string,
  actions: PropTypes.object,
  isPersDataAutoUpdateEnabled: PropTypes.bool,
  isWaitingRequest: PropTypes.bool,
  isFetchError: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isPersDataAutoUpdateEnabled: selectIsPersDataAutoUpdateEnabled(state),
  isWaitingRequest: selectIsWaitingRequest(state),
  isFetchError: selectIsFetchError(state),
  isModalOpened: selectIsModalOpened(state),
  notifications: selectNotifications(state),
  notificationDetails: selectNotificationDetails(state),
  isOnlyOnceVariantUpd: selectIsOnlyOnceVariantUpd(state),
  onceParams: selectEsiaUsisMainParamsOnce(state),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    setUsisData,
    initAfterCheckStatusEsia,
    toggleUsisModal,
    redirectByEsiaAutoUrl,
    setIsFetchError,
    removeNotificationPD,
    redirectByEsiaOnceUrl,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdateDataFromEsia);
